import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {NeoXChainHelper} from '@/helpers/NeoXChainHelper'

export type NeoLineTargetNetwork = 'n3' | 'neoX'

const NETWORK_LABELS: Record<NeoLineTargetNetwork, string> = {
  n3: 'Neo N3',
  neoX: 'Neo X',
}

export type TWrongNeoLineNetworkError = Error & {
  detectedNetwork: string | null
}

export const WrongNeoLineNetworkError = (
  target: NeoLineTargetNetwork,
  detectedNetwork: string | null = null
): TWrongNeoLineNetworkError => {
  const error = new Error(
    `NeoLine is on the wrong network${
      detectedNetwork ? ` (${detectedNetwork})` : ''
    }. Open NeoLine, switch to ${NETWORK_LABELS[target]}, then connect again.`
  ) as TWrongNeoLineNetworkError

  error.name = 'WrongNeoLineNetworkError'
  error.detectedNetwork = detectedNetwork

  return error
}

export const isWrongNeoLineNetworkError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'WrongNeoLineNetworkError'

export const wrongNeoLineNetworkDetected = (error: unknown): string | null =>
  isWrongNeoLineNetworkError(error)
    ? (error as TWrongNeoLineNetworkError).detectedNetwork
    : null

/** How NeoLine names the network it is on, when it reports a name at all. */
const NETWORK_NAME_LABELS: Record<string, string> = {
  mainnet: 'Neo N3 Mainnet',
  testnet: 'Neo N3 Testnet',
  n3mainnet: 'Neo N3 Mainnet',
  n3testnet: 'Neo N3 Testnet',
  neoxmainnet: 'Neo X Mainnet',
  neoxtestnet: 'Neo X Testnet',
  neox: 'Neo X',
}

export const neoLineNetworkNameLabel = (
  name: string | null | undefined
): string | null =>
  typeof name === 'string'
    ? NETWORK_NAME_LABELS[
        name
          .trim()
          .toLowerCase()
          .replace(/[\s_-]/g, '')
      ] ?? null
    : null

export const neoLineTargetLabel = (
  target: NeoLineTargetNetwork,
  network: TWalletNetwork
): string =>
  `${NETWORK_LABELS[target]} ${network === 'testnet' ? 'Testnet' : 'Mainnet'}`

export const neoLineNetworkLabel = (
  chainId: number | null | undefined
): string | null => {
  if (chainId === 3) return 'Neo N3 Mainnet'
  if (chainId === 6) return 'Neo N3 Testnet'

  const neoXNetworks: TWalletNetwork[] = ['mainnet', 'testnet']

  for (const network of neoXNetworks) {
    try {
      if (NeoXChainHelper.chainId(network) === chainId) {
        return network === 'testnet' ? 'Neo X Testnet' : 'Neo X Mainnet'
      }
    } catch {
      // That Neo X network is not configured — nothing to match against.
    }
  }

  return null
}

const chainFamilyOfLabel = (
  label: string | null
): NeoLineTargetNetwork | null => {
  if (label === null) return null

  return label.startsWith(NETWORK_LABELS.neoX) ? 'neoX' : 'n3'
}

export const neoLineChainFamily = (
  chainId: number | null | undefined
): NeoLineTargetNetwork | null =>
  chainFamilyOfLabel(neoLineNetworkLabel(chainId))

export const neoLineNameFamily = (
  name: string | null | undefined
): NeoLineTargetNetwork | null =>
  chainFamilyOfLabel(neoLineNetworkNameLabel(name))

export const isNeoXAddress = (address: string) =>
  /^0x[a-fA-F0-9]{40}$/.test(address)

export const isNeoN3Address = (address: string) =>
  /^N[a-zA-Z0-9]{25,34}$/.test(address)

export const validateNeoLineAddressNetwork = (
  address: string,
  target: NeoLineTargetNetwork
) => {
  if (target === 'neoX' && !isNeoXAddress(address)) {
    throw WrongNeoLineNetworkError('neoX', NETWORK_LABELS.n3)
  }

  if (target === 'n3' && isNeoXAddress(address)) {
    throw WrongNeoLineNetworkError('n3', NETWORK_LABELS.neoX)
  }
}

export const expectedNeoLineChainId = (
  target: NeoLineTargetNetwork,
  network: TWalletNetwork
): number => {
  if (target === 'neoX') {
    return NeoXChainHelper.chainId(network)
  }

  return network === 'testnet' ? 6 : 3
}

export const validateNeoLineExtensionChainId = (
  chainId: number | undefined,
  target: NeoLineTargetNetwork,
  network: TWalletNetwork,
  reportedNetworkName?: string
) => {
  const expected = expectedNeoLineChainId(target, network)

  if (chainId === expected) return

  const reported =
    neoLineNetworkLabel(chainId) ?? neoLineNetworkNameLabel(reportedNetworkName)

  // NeoLine does not always answer with a chain id — the N3 dAPI leaves it out
  // while the extension sits on a Neo X chain, for one. The network name it
  // gives is then the only word on where the wallet is, so take it.
  if (
    (chainId === undefined || chainId === null) &&
    reported === neoLineTargetLabel(target, network)
  ) {
    return
  }

  console.error('[NeoLine] wrong network', {
    target,
    network,
    chainIdReported: chainId,
    chainIdExpected: expected,
    networkReported: reportedNetworkName,
  })

  throw WrongNeoLineNetworkError(target, reported)
}
